import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuX } from 'react-icons/lu';

// Ventana estándar (5-oct-2026). Celu: hoja desde abajo. Compu: centrada.
// Título y X fijos arriba, contenido con scroll en el medio y pie fijo abajo.
// El scroll reserva el mismo espacio a los dos lados, así el contenido queda
// centrado (antes la barra de scroll comía margen solo a la derecha).
// 22-oct-2026: se dibuja en <body> (portal) para que una ventana abierta desde
// otra que está centrada con transform no quede encerrada ni cortada.
// 27-oct-2026:
//  · En la compu la ventana va con el mismo zoom 1.2 que el resto (estaba en <body>, fuera del zoom de <main>,
//    y la letra se veía más chica que la pantalla de atrás).
//  · lateral: en la compu se abre como panel a la derecha, sin tapar la pantalla: se sigue viendo y usando la
//    agenda o la lista, y tocar otro trabajo cambia el panel. En el celu sigue siendo la hoja desde abajo.
const COMPU = '(min-width: 1024px)';

function useCompu() {
    const [si, setSi] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(COMPU).matches);
    useEffect(() => {
        const m = window.matchMedia?.(COMPU);
        if (!m) return undefined;
        const f = () => setSi(m.matches);
        m.addEventListener?.('change', f);
        return () => m.removeEventListener?.('change', f);
    }, []);
    return si;
}

function Cuerpo({ titulo, subtitulo, onCerrar, pie, children }) {
    return (
        <>
            <div data-hoja-handle className="shrink-0 flex items-center gap-3 px-5 pt-4 pb-3 border-b border-black/[0.06] dark:border-white/[0.06]">
                <div className="flex-1 min-w-0">
                    <h2 className="text-body-lg font-black text-ink leading-tight truncate">{titulo}</h2>
                    {subtitulo && <p className="text-caption text-muted truncate">{subtitulo}</p>}
                </div>
                <button type="button" onClick={onCerrar} aria-label="Cerrar"
                    className="w-9 h-9 shrink-0 rounded-xl bg-chip text-muted flex items-center justify-center active:scale-95"><LuX size={17} /></button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 [scrollbar-gutter:stable_both-edges]">
                {children}
            </div>
            {pie && (
                <div className="shrink-0 px-5 pt-3 pb-5 md:pb-4 border-t border-black/[0.06] dark:border-white/[0.06]">{pie}</div>
            )}
        </>
    );
}

function Lateral({ titulo, onCerrar, ...resto }) {
    // La pantalla de atrás le deja lugar al panel (index.css: body[data-panel-lateral] main) y Esc lo cierra
    useEffect(() => {
        const n = Number(document.body.dataset.panelLateral || 0) + 1;
        document.body.dataset.panelLateral = String(n);
        return () => {
            const m = Number(document.body.dataset.panelLateral || 1) - 1;
            if (m > 0) document.body.dataset.panelLateral = String(m);
            else delete document.body.dataset.panelLateral;
        };
    }, []);
    useEffect(() => {
        const f = (e) => {
            // Si hay otra ventana abierta encima (reprogramar, cerrar trabajo…), Esc es de esa
            if (e.key === 'Escape' && !document.querySelector('[data-modal-centrado]')) onCerrar?.();
        };
        window.addEventListener('keydown', f);
        return () => window.removeEventListener('keydown', f);
    }, [onCerrar]);
    return createPortal(
        <aside className="panel-lateral fixed top-0 right-0 z-[1900] bg-card flex flex-col border-l border-black/[0.08] dark:border-white/[0.08] shadow-2xl"
            role="dialog" aria-label={titulo}>
            <Cuerpo titulo={titulo} onCerrar={onCerrar} {...resto} />
        </aside>
    , document.body);
}

export default function ModalShell({ titulo, subtitulo, onCerrar, pie, ancho = 'md:max-w-lg', alto = '', lateral = false, children }) {
    const compu = useCompu();
    if (lateral && compu) return <Lateral titulo={titulo} subtitulo={subtitulo} onCerrar={onCerrar} pie={pie}>{children}</Lateral>;
    return createPortal(
        <div data-modal-centrado className="fixed inset-0 z-[2000] bg-black/60 dark:bg-black/75 flex items-end md:items-center justify-center md:p-6 md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6" onClick={onCerrar}>
            <div className={`modal-caja w-full ${ancho} ${alto} max-h-[calc(var(--vh,1vh)*92)] md:max-h-[calc(var(--vh,1vh)*88)] bg-card rounded-t-3xl md:rounded-3xl flex flex-col overflow-hidden shadow-2xl`}
                onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={titulo}>
                <Cuerpo titulo={titulo} subtitulo={subtitulo} onCerrar={onCerrar} pie={pie}>{children}</Cuerpo>
            </div>
        </div>
    , document.body);
}
